import { useLayoutEffect, useState } from "react";

/**
 * Observes an element's width and re-renders only when `select(width)` changes, so
 * callers subscribe to a layout bucket instead of every resize pixel. `select` must be
 * stable (module-level or memoized) and return a primitive.
 */
export function useElementWidthBucket<T extends HTMLElement, V extends string | number | boolean>(
  select: (widthPx: number) => V,
): readonly [(node: T | null) => void, V] {
  const [node, setNode] = useState<T | null>(null);
  const [bucket, setBucket] = useState<V>(() => select(0));

  useLayoutEffect(() => {
    if (!node) return;
    const update = () => setBucket(select(node.clientWidth));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, select]);

  return [setNode, bucket] as const;
}
